import { t } from 'i18next';
import { get } from 'lodash';

import { MessageDialogTypes } from '../components/MessageDialog/MessageDialog';
import { checkCaptain } from '../selectors/screen';
import { MessageType, TelegramPrefix as TelexPrefix } from '../service/constants';
import { OtherError } from '../service/errors/OtherError';
import ErrorLogsSenderInstanse from '../service/ErrorLogsSender';
import { dataBufferToBlob, uint8ArrayToBlob } from '../service/ImageUtils';
import { sortObjByField } from '../service/sort';
import { getErrorMessage, getFileExtensions, isAndroid } from '../service/utils';
import { createImage, getImageUrl } from './images';
import { showModal, TYPES } from './modal';
import { showConfirmDialog } from './messageDialog';
import { setLoadingState } from './progressIndicator';

export const SIGN_TELEX = 'SIGN_TELEX';
export const UPDATE_TELEX = 'UPDATE_TELEX';
export const UPDATE_TELEX_ATTACHMENT = 'UPDATE_TELEX_ATTACHMENT';
const MAX_TELEX_SIGN_RESOLUTION = 400;
const TELEX_ATTACHMENT_FOLDER = 'telexAttachments';

export function createTelex(id, text, contentType = 'text/plain') {
    return (dispatch, getState, { plugins }) => {
        const blob = new Blob([text], { type: contentType });

        return plugins.file.write(id, blob).then(() => {
            return id;
        });
    };
}

function fileConverter(data, contentType) {
    if (data instanceof Uint8Array) {
        return uint8ArrayToBlob(data, contentType);
    } else {
        return dataBufferToBlob(data.data, contentType);
    }
}

export function createTelexAttachment({ id, data, contentType = 'application/pdf' }, dispatch, plugins) {
    const blob = fileConverter(data, contentType);

    return plugins.file[isAndroid() ? 'writeAndroidDownloadFile' : 'write'](
        `${TELEX_ATTACHMENT_FOLDER}_${id}${getFileExtensions(contentType)}`,
        blob,
        TELEX_ATTACHMENT_FOLDER
    ).then(() => id);
}

export function downloadTelexAttachment(id, telexId) {
    return (dispatch, getState, { apiManager }) => {
        dispatch(setLoadingState(true, t('messages.downloadAttachment')));

        return apiManager
            .getTelegramAttachment(id)
            .then((record) => ({ telexId, ...record }))
            .then((item) => dispatch({ type: UPDATE_TELEX_ATTACHMENT, data: new Array(item) }))
            .finally(() => dispatch(setLoadingState(false)));
    };
}

export function getAttachments(attachments = {}) {
    return Object.keys(attachments)
        .map((id) => {
            const { name, changeTime, contentType, isDownloaded } = attachments[id] || {};
            const extension = getFileExtensions(contentType);
            const disabled = !extension;

            return {
                id,
                name,
                changeTime,
                disabled,
                extension,
                isDownloaded,
            };
        })
        .sort(sortObjByField('name'));
}

export function getTelex({ id, name, showPreview = true, ofpId }) {
    return (dispatch, getState, { plugins }) => {
        return new Promise((res) => {
            const state = getState();
            const {
                telex,
                screen: { currentLeg },
                sendedData,
            } = state;
            const telegram = telex[id];
            const isCfp = telegram && telegram.name === 'CFP';
            const signComment = get(sendedData[currentLeg], 'signComment');
            const signatureName = isCfp ? get(sendedData[currentLeg], 'signature') : `sign_telegram_${id}`;
            const isCurrentUserCaptain = checkCaptain(state);
            const { attachments = {} } = telegram || {};

            return Promise.all([Promise.resolve(attachments), dispatch(preloadTelexSignatureUrl(signatureName))])
                .then(([attachments]) => {
                    const { images } = getState();
                    const signaturePreview = showPreview && signatureName && get(images, `[${signatureName}].url`);

                    return plugins.file.readFileAsString(`telegram_${id}`).then((text) => {
                        dispatch(
                            showModal(TYPES.TELEGRAM_VIEWER, {
                                isSupportTelex: true,
                                title: name,
                                message: text,
                                signaturePreview,
                                // Показываем только если не OFP или конкретная OFP
                                isPreviewEditable: !isCfp || !!ofpId,
                                signatureName: signatureName,
                                attachments,
                                ofpId,
                                ...(isCfp && { signComment }),
                                isCurrentUserCaptain,
                            })
                        );
                        res();
                    });
                })
                .catch((err) => {
                    console.log(err);
                    dispatch(
                        showConfirmDialog({
                            title: t('errors.title.showTelegram'),
                            message: t('errors.showTelegram'),
                            btnCancelVisibility: false,
                        })
                    );
                });
        });
    };
}

export function preloadTelexSignatureUrl(id) {
    return (dispatch, getState) => {
        const signature = getState().images[id];
        if (signature) {
            return dispatch(getImageUrl(id));
        }

        return Promise.resolve();
    };
}

export function signTelex(signature, signatureName = '') {
    return (dispatch, getState) => {
        return new Promise((resolve, reject) => {
            const state = getState();
            const { data, contentType } = signature;
            const id = signatureName && (signatureName.match(/[0-9]+$/g) || [])[0];
            const telegramId = get(state.telex[id], 'id');

            return (
                id && telegramId
                    ? dispatch(
                          createImage(signatureName, data, contentType, {
                              createImmediately: true,
                              rewrite: true,
                              needCompression: true,
                              maxResolution: MAX_TELEX_SIGN_RESOLUTION,
                          })
                      )
                    : Promise.reject()
            )
                .then(() => {
                    const {
                        images,
                        screen: { currentLeg },
                    } = getState();
                    const url = get(images, `[${signatureName}].url`);

                    dispatch({
                        type: SIGN_TELEX,
                        id: telegramId,
                        signature: signatureName,
                        signaturePreview: url,
                        legId: currentLeg,
                    });

                    return resolve(url);
                })
                .catch((err) => {
                    dispatch(
                        showConfirmDialog({
                            title: t('errors.title.signTelegram'),
                            message: t('errors.signTelegram'),
                            btnCancelVisibility: false,
                        })
                    );
                    console.log(err);
                    reject();
                });
        });
    };
}

export function showAttachment(id, onShow, extension, folder = TELEX_ATTACHMENT_FOLDER) {
    const fileName = `${TelexPrefix.ATTACHMENT}${id}`;
    return (dispatch, getState, { plugins }) => {
        plugins.pdf.show(fileName, onShow, extension, folder).catch(() => {
            setTimeout(
                () =>
                    dispatch(
                        showConfirmDialog({
                            title: t('errMessage'),
                            message: t('errors.openFile'),
                            btnCancelVisibility: false,
                            options: {
                                type: MessageDialogTypes.ERROR,
                            },
                        })
                    ),
                1000
            );
        });
    };
}

export function updateTelexes(taskId, telexes) {
    return (dispatch, getState, { apiManager, plugins }) => {
        dispatch({ type: UPDATE_TELEX, data: telexes });

        return Promise.allSettled(
            telexes.flatMap(
                //TODO Убрать проверку на !isDownloaded и оставить только получение attachment после перевода клиентов на версию 4.4.6
                (telex) =>
                    Object.entries(telex.attachments).map(([id, { isDownloaded }]) => {
                        if (!isDownloaded) {
                            return apiManager.getTelegramAttachment(id).then((record) => ({
                                telexId: telex.id,
                                ...record,
                            }));
                        }

                        return Promise.resolve({ telexId: telex.id, id });
                    })
            )
        )
            .then((data) => {
                return Promise.all(
                    data.reduce((res, { value }) => {
                        const hasValue = !!value;

                        if (hasValue) {
                            const { telexId, id, data, contentType } = value;

                            if (value.data) {
                                createTelexAttachment({ id, data, contentType }, dispatch, plugins);
                            }

                            res.push({ telexId, id });
                        }

                        return res;
                    }, [])
                ).catch((err) => {
                    const errorWithCustomMessage = new Error(getErrorMessage(err, MessageType.CREATE_TELEX_ATTACHMENT));
                    const error = new OtherError(errorWithCustomMessage);
                    ErrorLogsSenderInstanse.addLog(error);
                });
            })
            .then((data) => dispatch({ type: UPDATE_TELEX_ATTACHMENT, data }));
    };
}
